import { FieldFactory, Form } from '@sravni/cosago-react-library/lib/components';
import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import { Button } from '@sravni/react-design-system';
import type { FC } from 'react';
import React, { useMemo } from 'react';

import { FormStepId } from 'shared/config/formStepId';
import { useAppSelector } from 'shared/lib';
import { useSyncFormFieldsToQueryParams } from 'shared/lib/useSyncFormFieldsToQueryParams';
import { FormBlockLabel } from 'shared/ui/FormBlockLabel';

import type { TLandingFormFields } from 'entities/creditForm';
import { useDevFillMockDataTool } from 'entities/creditForm/lib/useDevFillMockDataTool';
import { isLoggedSelector } from 'entities/user';

import { AuthenticationEsia, useIsShowAuthenticationEsia } from 'features/AuthenticationEsia';
import { useCarInfoDictionariesLoader, useIsShowAutoFields } from 'features/UpdateCreditPurposeInfo';
import { useRegionLocationRouteLoader, useUpdateGender } from 'features/UpdatePassportInfo';

import { EsiaTextsConfig, StepsLabels, SubmitButtonText } from 'widgets/AnketaWidget/ui/LandingInfo/LandingInfo.texts';

import type { FormBodyProps } from '../types';

import styles from './FormBody.module.scss';

export const FormBody: FC<FormBodyProps> = ({ formLabels, formFieldsControls, isLoading }) => {
  const { watch } = useFormContext<TLandingFormFields>();
  const checkIsShowEsiaLoginBlock = useIsShowAuthenticationEsia();
  const isAuth = useAppSelector(isLoggedSelector);
  const FieldConstructor = useMemo(
    () => FieldFactory(formLabels, formFieldsControls),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formLabels],
  );
  const isShowEsia = useMemo(
    () => checkIsShowEsiaLoginBlock(FormStepId.ContactsInfo, !isAuth),
    [checkIsShowEsiaLoginBlock, isAuth],
  );

  useSyncFormFieldsToQueryParams({
    amount: watch('amount'),
    period: watch('period.value'),
    purpose: watch('purpose.value'),
  });

  useUpdateGender();
  useRegionLocationRouteLoader();
  useCarInfoDictionariesLoader();

  useDevFillMockDataTool<TLandingFormFields>({
    type: 'seoLandingData',
    stepLabel: 'Лендинг витрины',
  });

  const { isShowAutoYearField, isShowAutoModelField, isShowAutoFields } = useIsShowAutoFields();

  return (
    <Form.Block>
      <FormBlockLabel>{StepsLabels.creditPurpose}</FormBlockLabel>
      <Form.Row>
        <FieldConstructor type="purpose" />

        <FieldConstructor type="amount" />

        <FieldConstructor type="period" />
      </Form.Row>
      <Form.Row colWidths={[8]}>
        <FieldConstructor type="region" />
      </Form.Row>
      {isShowAutoFields && (
        <>
          <Form.Row>
            <FieldConstructor type="initialFee" />

            <FieldConstructor type="autoBrand" />

            {isShowAutoModelField && <FieldConstructor type="autoModel" />}
          </Form.Row>
          {isShowAutoYearField && (
            <Form.Row>
              <FieldConstructor type="autoYear" />
            </Form.Row>
          )}
        </>
      )}
      <FormBlockLabel>{StepsLabels.contacts}</FormBlockLabel>
      {isShowEsia && (
        <Form.Row colWidths={[12]}>
          <AuthenticationEsia
            step={FormStepId.ContactsInfo}
            config={EsiaTextsConfig}
            className={styles.esiaBlock}
            buttonProps={{
              size: 52,
            }}
            isShowIcon
            isForceShowEsia
          />
        </Form.Row>
      )}
      <Form.Row colWidths={[8]}>
        <FieldConstructor type="fullName" />
      </Form.Row>
      {isAuth && (
        <Form.Row>
          <FieldConstructor type="email" />
          <FieldConstructor type="mobilePhone" />
          <Button
            loading={isLoading}
            className={styles.submitButton}
            size={52}
            variant="primary"
            type="submit"
            block
          >
            {SubmitButtonText}
          </Button>
        </Form.Row>
      )}
    </Form.Block>
  );
};
